/**
 * permissionLabels — bilingual labels for RBAC permission CODES.
 *
 * B29 Lane A (UI-PERM-GUARDS-01 / PERM-DENIED-AR-01). The backend rejects a
 * missing permission with `403 {"detail": "Missing required permission: <code>"}`.
 * Both the axios interceptor (toasts) and the disabled-with-reason tooltips need
 * a human label for the code, in the UI language. There was no label dictionary
 * in the frontend before this file; it covers the codes touched by the
 * accounting UAT and falls back to the raw code for anything else.
 *
 * Rule: UI guards read PERMISSION CODES (usePermissions().has(code)) — never the
 * legacy `users.role` string and never role codes. The `holder` hint below is
 * display copy only ("who normally has this"), not a gate.
 */

export type UiLang = 'ar' | 'en'

export interface PermissionLabel {
  ar: string
  en: string
  /** Display hint: the role that normally holds this permission. */
  holder?: { ar: string; en: string }
}

// Arabic holder copy is stored in its prepositional form ("للمحاسب الأول")
// so the tooltip reads "متاحة للمحاسب الأول" without a dangling "لـ".
const SENIOR_ACCOUNTANT = { ar: 'للمحاسب الأول', en: 'senior accountant' }
const ADMIN = { ar: 'لمدير النظام', en: 'administrator' }

export const PERMISSION_LABELS: Record<string, PermissionLabel> = {
  // PW-2 (BOQ-RATE-PERMISSION-SPLIT-01). `boq.update_prices` was seeded and
  // granted to senior_estimator / project_director from the beginning and
  // guarded nothing; PW-2 attaches it to the rate write. Master ruling (b),
  // 2026-10-05: pricing belongs to ESTIMATION, so the holder hint names the
  // senior estimator and project_manager is deliberately NOT granted it.
  'boq.update_prices': {
    ar: 'تعديل أسعار بنود جدول الكميات',
    en: 'BOQ unit-rate editing',
    holder: { ar: 'لكبير المقدرين', en: 'senior estimator' },
  },
  'boq.update_quantities': {
    ar: 'تعديل كميات جدول الكميات',
    en: 'BOQ quantity editing',
  },
  'invoice.approve': { ar: 'اعتماد الفواتير', en: 'invoice approval', holder: SENIOR_ACCOUNTANT },
  'invoice.update': { ar: 'تعديل الفواتير وتسجيل الدفعات', en: 'invoice update / payment recording', holder: SENIOR_ACCOUNTANT },
  'invoice.create': { ar: 'إنشاء الفواتير', en: 'invoice creation' },
  'ap.invoice.reverse': { ar: 'عكس قيود الفواتير', en: 'invoice reversal', holder: SENIOR_ACCOUNTANT },
  'ipc.create': { ar: 'إنشاء شهادات الدفع', en: 'payment certificate creation', holder: SENIOR_ACCOUNTANT },
  'ipc.update': { ar: 'تعديل شهادات الدفع', en: 'payment certificate update', holder: SENIOR_ACCOUNTANT },
  'ipc.approve': { ar: 'اعتماد شهادات الدفع', en: 'payment certificate approval', holder: SENIOR_ACCOUNTANT },
  'asset.view': { ar: 'عرض الأصول الثابتة', en: 'fixed-asset viewing' },
  'asset.manage': { ar: 'إدارة سجل الأصول الثابتة', en: 'fixed-asset register management', holder: SENIOR_ACCOUNTANT },
  'asset.post': { ar: 'ترحيل الإهلاك واستبعاد الأصول', en: 'depreciation posting / asset disposal', holder: SENIOR_ACCOUNTANT },
  'settings.update': { ar: 'تعديل إعدادات الشركة', en: 'company settings update', holder: ADMIN },
  'tax.config.edit': { ar: 'تعديل إعدادات الضرائب', en: 'tax configuration', holder: SENIOR_ACCOUNTANT },
  'subcontractor.payment.create': { ar: 'إنشاء شهادات دفع المقاولين الفرعيين', en: 'subcontractor payment certificate creation', holder: SENIOR_ACCOUNTANT },
}

const MISSING_PERMISSION_PREFIX = 'Missing required permission:'

/** Extract the permission code from a backend 403 detail, or null. */
export function parseMissingPermission(detail: unknown): string | null {
  if (typeof detail !== 'string' || !detail.startsWith(MISSING_PERMISSION_PREFIX)) return null
  const code = detail.slice(MISSING_PERMISSION_PREFIX.length).trim()
  return code || null
}

/** Human label for a code (falls back to the raw code). */
export function permissionLabel(code: string, lang: UiLang): string {
  const entry = PERMISSION_LABELS[code]
  return entry ? entry[lang] : code
}

/**
 * Tooltip copy for a DISABLED action (rule 3):
 *   ar: "يتطلب صلاحية اعتماد الفواتير — متاحة للمحاسب الأول"
 *   en: "Requires invoice approval permission (senior accountant)"
 */
export function permissionRequirementMessage(code: string, lang: UiLang): string {
  const entry = PERMISSION_LABELS[code]
  const label = entry ? entry[lang] : code
  const holder = entry?.holder?.[lang]
  if (lang === 'ar') {
    return holder ? `يتطلب صلاحية ${label} — متاحة ${holder}` : `يتطلب صلاحية ${label}`
  }
  return holder ? `Requires ${label} permission (${holder})` : `Requires ${label} permission`
}

// ---------------------------------------------------------------------------
// BUSINESS-RULE-MSG-AR-01 — backend business-rule rejections (400/403/409)
// whose `detail` is a fixed English sentence. Add future rules HERE: one entry
// per rule, matched by prefix (case-insensitive) so a suffix such as
// " (ipc 12)" still maps. Localised by the same lib/api.ts normaliser.
// ---------------------------------------------------------------------------

export interface BusinessRuleMessage {
  /** Stable id for tests/logs. */
  id: string
  /** Backend detail prefix (compared lower-cased, trimmed). */
  prefix: string
  ar: string
  en: string
}

export const BUSINESS_RULE_MESSAGES: BusinessRuleMessage[] = [
  {
    // bank.py / ipc_service / subcontractor_payment_service / supplier_invoice_service
    // / material request_service / purchase_order_service (PO-SELF-APPROVAL-SOD-01):
    // segregation of duties.
    id: 'segregation_of_duties',
    prefix: 'creator cannot approve/certify own entry',
    ar: 'لا يمكن للمُنشئ اعتماد أو تصديق مستنده (فصل المهام)',
    en: 'The creator cannot approve or certify their own document (segregation of duties)',
  },
  {
    // company_settings.py POST /logo legacy-role check (interim mapping; the
    // backend check itself is tracked as LEGACY-ROLE-GATE-01).
    id: 'logo_upload_legacy_role',
    prefix: 'Only admins and project managers can upload the logo.',
    ar: 'رفع الشعار متاح لمديري النظام ومديري المشاريع فقط',
    en: 'Only admins and project managers can upload the logo.',
  },
  {
    // company_settings.py DELETE /logo legacy-role check -- a DIFFERENT sentence
    // from the upload one above ("remove" vs "upload"), so it needs its own
    // entry or LogoUploader.handleDelete surfaces the raw English (review #1060
    // P3-4). Same interim mapping; backend check tracked as LEGACY-ROLE-GATE-01.
    id: 'logo_remove_legacy_role',
    prefix: 'Only admins and project managers can remove the logo.',
    ar: 'حذف الشعار متاح لمديري النظام ومديري المشاريع فقط',
    en: 'Only admins and project managers can remove the logo.',
  },
]

/** Match a backend detail against the business-rule table, or null. */
export function matchBusinessRule(detail: unknown): BusinessRuleMessage | null {
  if (typeof detail !== 'string') return null
  const needle = detail.trim().toLowerCase()
  if (!needle) return null
  return BUSINESS_RULE_MESSAGES.find((r) => needle.startsWith(r.prefix.toLowerCase())) ?? null
}

/** Localised copy for a matched business rule. */
export function businessRuleMessage(rule: BusinessRuleMessage, lang: UiLang): string {
  return lang === 'ar' ? rule.ar : rule.en
}

/**
 * Toast copy for a backend 403 "Missing required permission" (PERM-DENIED-AR-01):
 *   ar: "ليس لديك صلاحية لهذا الإجراء (اعتماد الفواتير)"
 *   en: "You do not have permission for this action (invoice approval)"
 */
export function permissionDeniedMessage(code: string, lang: UiLang): string {
  const label = permissionLabel(code, lang)
  return lang === 'ar'
    ? `ليس لديك صلاحية لهذا الإجراء (${label})`
    : `You do not have permission for this action (${label})`
}
